import React, { useEffect, useState, useMemo } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonLoading,
} from "@ionic/react";
import {
  Box,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Typography,
  TablePagination,
  InputAdornment,
  Card,
  CardContent,
  Avatar,
  Grid,
  TableSortLabel,
  useTheme,
  useMediaQuery,
  Button,
  IconButton,
  Stack,
  Chip,
  Divider,
  TableFooter,
} from "@mui/material";
import { Search, Inventory, CalendarToday, TrendingDown, TrendingUp, Refresh, KeyboardArrowDown } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getProductPerformance } from "../../../api/customers/customers";
import { MenuButton } from "../../../components/Menu";

interface ProductPerformance {
  product_id: string;
  name: string;
  price: number;
  category: string;
  type: string;
  instocks: number;
  qty: number;
  totalQtyPosted: number;
  totalQtySold: number;
  img_src: string;
  minSalePrice: number;
  purchasePrice: number;
  grade_letter: string;
  category_id: string;
  productCategory: string;
  totalTransactions: number;
  totalCustomers: number;
  totalSellingPrice: number;
  totalCommission: number;
  totalQuantitySold: number;

  totalnew_Commission: number;
  totalnew_costPrice: number;
  totalcostPrice: number;
  totalProfit: number;
  totalLoss: number;
}

type Order = 'asc' | 'desc';

interface HeadCell {
  id: keyof ProductPerformance;
  label: string;
  numeric: boolean;
  sortable: boolean;
}

const headCells: HeadCell[] = [
  { id: 'name', label: 'Service Name', numeric: false, sortable: true },
  { id: 'type', label: 'Type', numeric: false, sortable: true },
  { id: 'totalTransactions', label: 'Trans', numeric: true, sortable: true },
  { id: 'totalCustomers', label: 'Customers', numeric: true, sortable: true },
  { id: 'totalSellingPrice', label: 'PayOut', numeric: true, sortable: true },
  { id: 'totalCommission', label: 'Commission', numeric: true, sortable: true },
];

const ProductPerformancePage1: React.FC = () => {
  const [productPerformance, setProductPerformance] = useState<ProductPerformance[]>([]);
  const [filteredData, setFilteredData] = useState<ProductPerformance[]>([]);
  const [status, setStatus] = useState({ loading: false, error: false });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: null,
    end: null,
  });
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof ProductPerformance>('totalSellingPrice');
  const [refreshing, setRefreshing] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const safeNumber = (value: any): number => {
    if (value === null || value === undefined) return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const fetchProductPerformance = async (startDate?: string, endDate?: string) => {
    setStatus({ loading: true, error: false });
    setRefreshing(true);
    try {
      const params: any = {};
      if (startDate && endDate) {
        params.start = startDate;
        params.end = endDate;
      }
      const response = await getProductPerformance(params);
      const productData = response.data || response.data?.data || [];
      
      const processedData = productData.map((product: any) => ({
        ...product,
        price: safeNumber(product.price),
        instocks: safeNumber(product.instocks),
        qty: safeNumber(product.qty),
        totalQtyPosted: safeNumber(product.totalQtyPosted),
        totalQtySold: safeNumber(product.totalQtySold),
        minSalePrice: safeNumber(product.minSalePrice),
        purchasePrice: safeNumber(product.purchasePrice),
        totalTransactions: safeNumber(product.totalTransactions),
        totalCustomers: safeNumber(product.totalCustomers),
        totalSellingPrice: safeNumber(product.totalSellingPrice),
        totalCommission: safeNumber(product.totalCommission),
        totalQuantitySold: safeNumber(product.totalQuantitySold),
        totalnew_Commission: safeNumber(product.totalnew_Commission),
        totalnew_costPrice: safeNumber(product.totalnew_costPrice),
        totalcostPrice: safeNumber(product.totalcostPrice),
        totalProfit: safeNumber(product.totalProfit),
        totalLoss: safeNumber(product.totalLoss),
      }));
      
      setProductPerformance(processedData);
      setFilteredData(processedData);
    } catch (error) {
      console.error("Error fetching product performance:", error);
      setStatus({ loading: false, error: true });
    } finally {
      setStatus(prev => ({ ...prev, loading: false }));
      setRefreshing(false);
    }
  };

  const formatDateForAPI = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const startDate = dateRange.start ? formatDateForAPI(dateRange.start) : undefined;
    const endDate = dateRange.end ? formatDateForAPI(dateRange.end) : undefined;
    fetchProductPerformance(startDate, endDate);
  }, [dateRange]);

  useEffect(() => {
    let filtered = productPerformance;

    if (searchQuery) {
      filtered = filtered.filter((product) => {
        const name = (product.name || "").toLowerCase();
        const category = (product.category || "").toLowerCase();
        const type = (product.type || "").toLowerCase();
        const productCategory = (product.productCategory || "").toLowerCase();
        const query = searchQuery.toLowerCase();

        return (
          name.includes(query) ||
          category.includes(query) ||
          type.includes(query) ||
          productCategory.includes(query)
        );
      });
    }

    setFilteredData(filtered);
    setPage(0);
  }, [searchQuery, productPerformance]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleDateRangeChange = (field: 'start' | 'end') => (date: Date | null) => {
    setDateRange(prev => ({ ...prev, [field]: date }));
  };

  const handleRequestSort = (property: keyof ProductPerformance) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: "currency",
      currency: "NGN",
    }).format(amount);
  };

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      let aValue: any = a[orderBy];
      let bValue: any = b[orderBy];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, order, orderBy]);

  const tableTotals = {
    totalSellingPrice: filteredData.reduce((sum, p) => sum + safeNumber(p.totalSellingPrice), 0),
    totalCommission: filteredData.reduce((sum, p) => sum + safeNumber(p.totalCommission), 0),
    totalTransactions: filteredData.reduce((sum, p) => sum + safeNumber(p.totalTransactions), 0),
    totalCustomers: filteredData.reduce((sum, p) => sum + safeNumber(p.totalCustomers), 0),
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <IonPage className="pushDown">
        <IonHeader>
          <IonToolbar>
            <MenuButton />
            <IonTitle>Service Performance</IonTitle>
          </IonToolbar>
        </IonHeader>

        <IonContent className="ion-padding">
          <IonLoading
            isOpen={status.loading}
            message={"Analyzing metrics..."}
            spinner="circles"
          />

          <Box sx={{ mb: 4 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                  Service Metrics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Revenue and activity breakdown by service
                </Typography>
              </Box>
              <Button 
                variant="outlined" 
                startIcon={<Refresh sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />} 
                onClick={() => fetchProductPerformance(dateRange.start ? formatDateForAPI(dateRange.start) : undefined, dateRange.end ? formatDateForAPI(dateRange.end) : undefined)} 
                sx={{ borderRadius: 2 }}
              >
                Refresh
              </Button>
            </Stack>

            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <DatePicker
                        label="Start Date"
                        value={dateRange.start}
                        onChange={handleDateRangeChange('start')}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                      <DatePicker
                        label="End Date"
                        value={dateRange.end}
                        onChange={handleDateRangeChange('end')}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search services..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><Search color="primary" /></InputAdornment>,
                        sx: { borderRadius: 2 }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          {/* Desktop Table View */}
          <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    {headCells.map((headCell) => (
                      <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        sx={{ fontWeight: 'bold', py: 2 }}
                        sortDirection={orderBy === headCell.id ? order : false}
                      >
                        {headCell.sortable ? (
                          <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={() => handleRequestSort(headCell.id)}
                          >
                            {headCell.label}
                          </TableSortLabel>
                        ) : headCell.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((product) => (
                    <TableRow key={product.product_id} hover>
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar sx={{ bgcolor: 'info.light', fontWeight: 'bold', width: 32, height: 32, fontSize: '0.875rem' }}>
                            {product.name?.charAt(0)}
                          </Avatar>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{product.name}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell><Chip label={product.type} size="small" variant="outlined" /></TableCell>
                      <TableCell align="right">{product.totalTransactions}</TableCell>
                      <TableCell align="right">{product.totalCustomers}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main', fontWeight: 'bold' }}>{formatCurrency(product.totalSellingPrice)}</TableCell>
                      <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{formatCurrency(product.totalCommission)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter sx={{ bgcolor: 'grey.100' }}>
                  <TableRow>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>TOTALS</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{tableTotals.totalTransactions}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{tableTotals.totalCustomers}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.dark' }}>{formatCurrency(tableTotals.totalSellingPrice)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>{formatCurrency(tableTotals.totalCommission)}</TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
              <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
              />
            </TableContainer>
          </Box>

          {/* Mobile Card List View */}
          <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
            {/* Mobile Summary Card */}
            <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>OVERALL TOTALS ({filteredData.length} Services)</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Total PayOut</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{formatCurrency(tableTotals.totalSellingPrice)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>Total Commission</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{formatCurrency(tableTotals.totalCommission)}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((product) => (
              <Card key={product.product_id} sx={{ mb: 2, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'info.light', fontWeight: 'bold' }}>{product.name?.charAt(0)}</Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{product.name}</Typography>
                      <Chip label={product.type} size="small" sx={{ mt: 0.5, height: 20, fontSize: '0.625rem' }} />
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{product.totalTransactions}</Typography>
                      <Typography variant="caption" color="text.secondary">Transactions</Typography>
                    </Box>
                  </Stack>

                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Box sx={{ p: 1.5, bgcolor: 'success.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">PayOut</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.dark' }}>{formatCurrency(product.totalSellingPrice)}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ p: 1.5, bgcolor: 'primary.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary" display="block">Commission</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>{formatCurrency(product.totalCommission)}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
            
            <TablePagination
              component="div"
              count={filteredData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </Box>

          {filteredData.length === 0 && !status.loading && (
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <Inventory sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No performance data found</Typography>
            </Box>
          )}
        </IonContent>
      </IonPage>
    </LocalizationProvider>
  );
};

export default ProductPerformancePage1;
